import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { BlockUI } from 'ng-block-ui';
import { zip as observableZip } from 'rxjs';
import { LoggedInUserService } from '../../auth/logged.in.user';
import { SearchComponent } from '../../components/common/search/search.component';
import { Configuration } from '../../configuration';
import { CategoryAndLocationHelpers, Helpers } from '../../helpers';
import { AddCustomerHiddenDialogDto, AddCustomerSavedSearchDto, GetCustomerSavedSearchesDto } from '../../models/account/account.models';
import { SelectableCategory } from '../../models/categories/category.models';
import { CmsDialogDto } from '../../models/cms.dialogs/cms.dialogs.models';
import { DateRangeDto, DateRangeModel } from '../../models/common/date.range';
import { SelectableItem } from '../../models/common/selectableItem';
import { LocationsByRegionDto, SelectableLocation, SelectableRegion } from '../../models/locations/location.models';
import { NoticeEnumsToSelectList, NoticeStatus } from '../../models/notices/notice.enums';
import { SortOption } from '../../models/tenders/sort.option';
import { TenderType, TenderTypeToSelectList } from '../../models/tenders/tender.enums';
import { TenderSearchRequest, TenderSearchStorage, TenderSummaryDto } from '../../models/tenders/tender.models';
import { AccountService } from '../../services/account.service';
import { CategoryService } from '../../services/category.service';
import { CmsDialogService } from '../../services/cms.dialog.service';
import { LocationService } from '../../services/location.service';
import { TenderService } from '../../services/tender.service';
import { PaidFeatureUpsellService } from '../../services/paid.feature.upsell.service';

@Component({
    selector: 'tenders/search',
    template: require('./search.component.html'),
    styles: [require('./search.component.css')]
})
export class TenderSearchComponent extends SearchComponent<TenderSearchRequest,
TenderSummaryDto> implements OnInit {
    types: Array<SelectableItem<TenderType>>;
    statuses: Array<SelectableItem<NoticeStatus>>;

    options: Array<SelectableItem<number>>;

    categories: Array<SelectableCategory>;
    locationsByRegion: LocationsByRegionDto;

    sortOptions = <SortOption[]>[];
    selectedSortOption: SortOption;
    sortOptionId: number;

    datePickerOptions: any;

    resultOptions;

    subscribed: boolean;

    pageSizes = [10, 25, 50];

    publishedDate = new DateRangeModel();
    expiredDate = new DateRangeModel();
    registerInterestDate = new DateRangeModel();
    submitDocumentsDeadline = new DateRangeModel();
    contractStartDate = new DateRangeModel();
    contractEndDate = new DateRangeModel();

    idErrorMatcher: ErrorStateMatcher;

    supplierId: number;

    dialogName: string;
    dialogHeader: string;
    dialogBody: string;

    csvDialogHeader: string;
    csvDialogBody: string;
    csvDialogHidden: boolean;
    csvDialogId: number;

    dialogNameFree: string;
    dialogHeaderFree: string;
    dialogBodyFree: string;

    saveFreeDialogName: string;
    saveFreeDialogHeader: string;
    saveFreeDialogBody: string;

    isExpanded: boolean;
    showSaveSearch: boolean;
    hasSearched: boolean = false;
    activeTab = 'details';
    selectedTabIndex: number = 0;

    savedSearches: GetCustomerSavedSearchesDto[];
    keyword: string;

    categoryFilter: string;

    private requestStorageKey = 'tenderSearchRequest';
    private readonly fromUrlKey = 'searchTenderId';

    searchName: string;
    clicked: boolean;

    @BlockUI('tender-results-block') private resultsBlock;
    @BlockUI('tender-search-block') private paramsBlock;

    @ViewChild('resultsPage', { static: false }) private resultsView;

    constructor(private readonly tenderService: TenderService, helpers: Helpers, configuration: Configuration,
        private readonly categoryService: CategoryService, private readonly locationService: LocationService,
        loggedInUserService: LoggedInUserService, private readonly locCatHelpers: CategoryAndLocationHelpers,
        private readonly cmsDialogService: CmsDialogService, private readonly accountService: AccountService,
        private readonly paidFeatureUpsellService: PaidFeatureUpsellService) {
        super(new TenderSearchRequest(configuration), tenderService, helpers);

        var user = loggedInUserService.getCurrentUser();

        this.subscribed = !user.canAdHoc('notSubscribed');
        this.supplierId = user.supplierId;

        this.isExpanded = true;
        this.showSaveSearch = false;
        this.types = TenderTypeToSelectList.getList();
        this.setSelectedTypes();
        this.statuses = NoticeEnumsToSelectList.getStatuses();

        this.publishedDate = new DateRangeModel();
        this.expiredDate = new DateRangeModel();
        this.registerInterestDate = new DateRangeModel();
        this.submitDocumentsDeadline = new DateRangeModel();
        this.contractStartDate = new DateRangeModel();
        this.contractEndDate = new DateRangeModel();

        this.clicked = false;

        this.sortOptionId = 1;

        this.datePickerOptions = {
            dateFormat: 'dd/mm/yyyy'
        };

        this.selectedSortOption = {
            id: 1,
            sortProperty: 'DeadlineDate',
            descending: false
        };

        this.sortOptions = [
            this.selectedSortOption,
            {
                id: 2,
                sortProperty: 'DeadlineDate',
                descending: true
            },
            {
                id: 3,
                sortProperty: 'Type',
                descending: false
            },
            {
                id: 4,
                sortProperty: 'Title',
                descending: false
            },
            {
                id: 5,
                sortProperty: 'PublishedDate',
                descending: true
            },
            {
                id: 6,
                sortProperty: 'PublishedDate',
                descending: false
            },
        ];

        this.options = [
            {
                text: 'Nearest Deadline Date',
                value: 1
            },
            {
                text: 'Furthest Deadline Date',
                value: 2
            },
            {
                text: 'Type',
                value: 3
            },
            {
                text: 'Title',
                value: 4
            },
            {
                text: 'Most Recent Published',
                value: 5
            },
            {
                text: 'Oldest Published',
                value: 6
            },
        ];

        if (loggedInUserService.getCurrentUser().role === 'Supplier') {
            this.resultOptions = {
                availableButtons: ['print', 'bookmark', 'assign', 'sendToBidManagement']
            };
        }
        else {
            this.resultOptions = {
                availableButtons: ['print', 'bookmark']
            };
        }

        this.idErrorMatcher = new InvalidOnlyErrorMatcher();

        this.categories = new Array<SelectableCategory>();
    }

    ngOnInit() {
        this.paramsBlock.start();

        var categoryObservable = this.categoryService.getActiveCategories();
        var locationObservable = this.locationService.getActiveLocations();

        observableZip(categoryObservable,
            locationObservable,
            (categories: Array<SelectableCategory>, locations: LocationsByRegionDto) => ({ categories, locations }))
            .subscribe(results => {
                this.categories = results.categories;
                this.locationsByRegion = results.locations;

                const storedRequest = JSON.parse(localStorage.getItem(this.requestStorageKey)) as TenderSearchStorage;

                if (storedRequest) {
                    this.request.setValues(storedRequest);
                    this.publishedDate.setMomentDatesFromStorage(storedRequest.publishedDate);
                    this.expiredDate.setMomentDatesFromStorage(storedRequest.expiredDate);
                    this.registerInterestDate.setMomentDatesFromStorage(storedRequest.registerInterestDeadline);
                    this.submitDocumentsDeadline.setMomentDatesFromStorage(storedRequest.submitDocumentsDeadline);
                    this.contractStartDate.setMomentDatesFromStorage(storedRequest.contractStartDate);
                    this.contractEndDate.setMomentDatesFromStorage(storedRequest.contractEndDate);
                    this.locCatHelpers.setSelectedCategories(this.categories, storedRequest.categoryIds);
                    this.locCatHelpers.setSelectedLocations(this.locationsByRegion, storedRequest.locationIds);                    
                }

                this.getSavedSearches();

                this.paramsBlock.stop();
            });

        if (this.subscribed) {
            this.dialogName = 'Search Tenders Info - Subscribed';
        }
        else {
            this.dialogName = 'Search Tenders Info - Not Subscribed';            
        }

        this.dialogNameFree = 'Search Tenders - No Keywords Free';

        this.saveFreeDialogName = 'Search Tenders - Save Search Free';

        this.cmsDialogService.getByDescription(this.saveFreeDialogName).subscribe((result: CmsDialogDto) => {
            this.saveFreeDialogHeader = result.header;
            this.saveFreeDialogBody = result.body;
        });

        this.cmsDialogService.getByDescription(this.dialogName).subscribe((result: CmsDialogDto) => {
            this.dialogHeader = result.header;
            this.dialogBody = result.body;
        });

        this.cmsDialogService.getByDescription(this.dialogNameFree).subscribe((result: CmsDialogDto) => {
            this.dialogHeaderFree = result.header;
            this.dialogBodyFree = result.body;
        });

        this.cmsDialogService.getByDescription('Search Tenders CSV').subscribe((result: CmsDialogDto) => {
            this.csvDialogHeader = result.header;
            this.csvDialogBody = result.body;
            this.csvDialogHidden = result.hidden;
            this.csvDialogId = result.id;
        });

        var selectedId = localStorage.getItem(this.fromUrlKey);

        if (selectedId) {
            this.request.id = JSON.parse(selectedId) as number;
            localStorage.removeItem(this.fromUrlKey);
            this.search();
        }
    }

    private getSavedSearches() {
        var eventObservable = this.accountService.getCustomerSavedSearches();

        observableZip(
            eventObservable,
            (savedSearches: GetCustomerSavedSearchesDto[]) => ({ savedSearches }))
            .subscribe(results => {
                this.savedSearches = results.savedSearches.filter(x => !x.award);
            });
    }

    runSavedSearch(savedSearch: GetCustomerSavedSearchesDto) {

        this.resetParameters();

        this.request.buyerEmail = savedSearch.buyerEmail;
        this.request.buyerName = savedSearch.buyerName;
        this.request.buyerOrganisation = savedSearch.buyerOrganisation;
        this.request.categoryIds = savedSearch.categoryIds;
        this.request.contractEndDateFrom = savedSearch.contractEndDateFrom;
        this.request.contractEndDateTo = savedSearch.contractEndDateTo;
        this.request.contractStartDateFrom = savedSearch.contractStartDateFrom;
        this.request.contractStartDateTo = savedSearch.contractStartDateTo;
        this.request.expiredDateFrom = savedSearch.expiredDateFrom;
        this.request.expiredDateTo = savedSearch.expiredDateTo;
        this.request.keywords = savedSearch.keywords;
        this.request.excludeKeywords = savedSearch.excludeKeywords;
        this.request.locationIds = savedSearch.locationIds;
        this.request.ojeuId = savedSearch.ojeuId;
        this.request.publishedDateFrom = savedSearch.publishedDateFrom;
        this.request.publishedDateTo = savedSearch.publishedDateTo;
        this.request.registerInterestDeadlineFrom = savedSearch.registerInterestDeadlineFrom;
        this.request.registerInterestDeadlineTo = savedSearch.registerInterestDeadlineTo;
        this.request.status = savedSearch.status;
        this.request.submitDocumentsDeadlineFrom = savedSearch.submitDocumentsDeadlineFrom;
        this.request.submitDocumentsDeadlineTo = savedSearch.submitDocumentsDeadlineTo;
        this.request.types = savedSearch.types;
        this.request.noticeId = savedSearch.noticeId;
        this.request.excludeAssigned = savedSearch.excludeAssigned;
        this.request.excludeBookmarked = savedSearch.excludeBookmarked;
        this.request.excludeViewed = savedSearch.excludeViewed;

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.publishedDateFrom;
        dateRange.endDate = this.request.publishedDateTo;
        this.publishedDate.setMomentDatesFromStorage(dateRange);

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.expiredDateFrom;
        dateRange.endDate = this.request.expiredDateTo;
        this.expiredDate.setMomentDatesFromStorage(dateRange);

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.submitDocumentsDeadlineFrom;
        dateRange.endDate = this.request.submitDocumentsDeadlineTo;
        this.submitDocumentsDeadline.setMomentDatesFromStorage(dateRange);

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.contractStartDateFrom;
        dateRange.endDate = this.request.contractStartDateTo;
        this.contractStartDate.setMomentDatesFromStorage(dateRange);

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.contractEndDateFrom;
        dateRange.endDate = this.request.contractEndDateTo;
        this.contractEndDate.setMomentDatesFromStorage(dateRange);

        var dateRange = new DateRangeDto();
        dateRange.startDate = this.request.registerInterestDeadlineFrom;
        dateRange.endDate = this.request.registerInterestDeadlineTo;
        this.registerInterestDate.setMomentDatesFromStorage(dateRange);

        this.locCatHelpers.setSelectedCategories(this.categories, savedSearch.categoryIds);
        this.locCatHelpers.setSelectedLocations(this.locationsByRegion, savedSearch.locationIds);

        this.types.forEach((t: SelectableItem<TenderType>) => {
            var idx = this.request.types.indexOf(t.value);
            if (idx > -1) {
                t.selected = true;
            } else {
                t.selected = false;
            }
        });

        this.search();
    }

    ngOnDestroy() {
        $('#search-info-modal').modal('hide');
        $('#search-modal').modal('hide');
        document.body.classList.remove('tender-search-fit');
    }

    private updateSearchFitClass() {
        if (this.hasSearched && !this.isExpanded) {
            document.body.classList.add('tender-search-fit');
        } else {
            document.body.classList.remove('tender-search-fit');
        }
    }

    setSelectedTypes() {
        this.types.forEach((t: SelectableItem<TenderType>) => {

            if (this.subscribed) {
                if (t.value === TenderType.High || t.value === TenderType.Low || t.value == TenderType.PinHigh || t.value == TenderType.PinLow || t.value == TenderType.PrivateProject) {
                    t.selected = true;
                } else {
                    t.selected = false;
                }
            }
            else {
                if (t.value === TenderType.High || t.value === TenderType.Low || t.value == TenderType.PrivateProject) {
                    t.selected = true;
                } else {
                    t.selected = false;
                }

                var idx = this.request.types.indexOf(TenderType.PinHigh);
                if (idx != -1) {
                    this.request.types.splice(idx, 1);
                }
                
                var idx2 = this.request.types.indexOf(TenderType.PinLow);
                if (idx2 != -1) {
                    this.request.types.splice(idx2, 1);
                }
            }            
        });
    }
    
    sortOptionChanged() {
        var id = +this.sortOptionId;
        this.selectedSortOption = this.sortOptions.find((option: SortOption) => {
            return option.id === id;
        });

        this.request.sortProperty = this.selectedSortOption.sortProperty;
        this.request.descending = this.selectedSortOption.descending;

        this.search();
    }

    updateRequestCategoriesSelected() {
        this.categories.forEach((c: SelectableCategory) => {
            this.updateRequestCategory(c);

            c.children.forEach((child: SelectableCategory) => {
                this.updateRequestCategory(child);
            });
        });
    }

    updateRequestCategory(category: SelectableCategory) {
        var idx = this.request.categoryIds.indexOf(category.id);

        if (category.isSelected && idx === -1) {
            this.request.categoryIds.push(category.id);
        } else if (!category.isSelected && idx > -1) {
            this.request.categoryIds.splice(idx, 1);
        }
    }

    setChildrenSelected(category: SelectableCategory) {
        category.children.forEach((child: SelectableCategory) => {
            child.isSelected = category.isSelected;
            this.setChildrenSelected(child);
        });

        var idx = this.request.categoryIds.indexOf(category.id);

        if (category.isSelected && idx === -1) {
            this.request.categoryIds.push(category.id);
        } else if (!category.isSelected && idx > -1) {
            this.request.categoryIds.splice(idx, 1);
        }
    };

    changeRegionSelected(region: SelectableRegion, locationIds: number[]) {
        region.locations.forEach((location: SelectableLocation) => {
            location.isSelected = region.isSelected;
            this.changeLocationSelected(location);
        });
    }

    changeLocationSelected(location: SelectableLocation) {
        var idx = this.request.locationIds.indexOf(location.id);

        if (location.isSelected && idx === -1) {
            this.request.locationIds.push(location.id);
        } else if (!location.isSelected && idx > -1) {
            this.request.locationIds.splice(idx, 1);
        }
    }

    selectAllLocations() {
        this.locationsByRegion.regions.forEach((r: SelectableRegion) => {
            r.isSelected = true;
            this.changeRegionSelected(r, this.request.locationIds);
        });

        this.locationsByRegion.locationsWithoutRegion.forEach((l: SelectableLocation) => {
            l.isSelected = true;
            this.changeLocationSelected(l);
        });
    }

    changeTypeSelected(type) {
        var idx = this.request.types.indexOf(type);

        if (idx > -1) {
            this.request.types.splice(idx, 1);
        } else {
            this.request.types.push(type);
        }
    }

    searchRequested() {
        if (this.request.id && (this.request.id > 2147483647 || this.request.id < 0)) {
            return;
        }

        if (this.request.keywords.length == 0 && this.request.excludeKeywords.length == 0) {

            if (this.subscribed) {
                $('#keywords-modal').modal();
            }
            else {
                $('#keywords-free-modal').modal();
            }            
        }
        else {
            this.search();
        }
    }

    showSaveDialog() {
        $('#search-modal').modal();
    }

    showKeywordsTab() {
        $('#keywords-modal').modal('hide');
        this.selectedTabIndex = 1;
        this.activeTab = 'keywords';
    }

    onTabIndexChange(index: number) {
        this.selectedTabIndex = index;

        // Keep activeTab in sync (some logic still references it)
        switch (index) {
        case 0:
            this.activeTab = 'details';
            break;
        case 1:
            this.activeTab = 'keywords';
            break;
        case 2:
            this.activeTab = 'categories';
            break;
        case 3:
            this.activeTab = 'locations';
            break;
        case 4:
            this.activeTab = 'savedSearches';
            break;
        default:
            this.activeTab = 'details';
            break;
        }
    }

    toggleFilters(event?: any) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.isExpanded = !this.isExpanded;
        this.updateSearchFitClass();
    }

    saveSearch() {

        var publishedDateRange = this.publishedDate.convertDatesToDto();
        var registerInterestDateRange = this.registerInterestDate.convertDatesToDto();
        var submittedDateRange = this.submitDocumentsDeadline.convertDatesToDto();
        var expiredDateRange = this.expiredDate.convertDatesToDto();
        var contractStartDateRange = this.contractStartDate.convertDatesToDto();
        var contractEndDateRange = this.contractEndDate.convertDatesToDto();

        var dto = new AddCustomerSavedSearchDto();

        dto.publishedDateFrom = publishedDateRange.startDate;
        dto.publishedDateTo = publishedDateRange.endDate;

        dto.registerInterestDeadlineFrom = registerInterestDateRange.startDate;
        dto.registerInterestDeadlineTo = registerInterestDateRange.endDate;

        dto.contractStartDateFrom = contractStartDateRange.startDate;
        dto.contractStartDateTo = contractStartDateRange.endDate;

        dto.contractEndDateFrom = contractEndDateRange.startDate;
        dto.contractEndDateTo = contractEndDateRange.endDate;

        dto.submitDocumentsDeadlineFrom = submittedDateRange.startDate;
        dto.submitDocumentsDeadlineTo = submittedDateRange.endDate;

        dto.expiredDateFrom = expiredDateRange.startDate;
        dto.expiredDateTo = expiredDateRange.endDate;

        dto.buyerEmail = this.request.buyerEmail;
        dto.buyerName = this.request.buyerName;
        dto.buyerOrganisation = this.request.buyerOrganisation;
        dto.categoryIds = this.request.categoryIds;

        dto.description = this.searchName;

        dto.keywords = this.request.keywords;
        dto.excludeKeywords = this.request.excludeKeywords;
        dto.locationIds = this.request.locationIds;
        dto.ojeuId = this.request.ojeuId;
        dto.status = this.request.status;
        dto.types = this.request.types;
        dto.id = this.request.id;
        dto.noticeId = this.request.noticeId;
        dto.award = false;
        dto.excludeViewed = this.request.excludeViewed;
        dto.excludeAssigned = this.request.excludeAssigned;
        dto.excludeBookmarked = this.request.excludeBookmarked;

        this.accountService.addCustomerSavedSearch(dto).subscribe(() => {
            $('#search-modal').modal('hide');
            this.paramsBlock.stop();
            this.resultsBlock.stop();
            this.getSavedSearches();
            this.showSaveSearch = false;
            this.resetParameters();
        }, () => {
        });

    }

    search() {
        $('#keywords-modal').modal('hide');

        if (this.request.types.length === 0) {
            return;
        }

        if ((this.request.locationIds.length === 0 || this.request.categoryIds.length === 0) && !this.subscribed) {
            return;
        }

        if (!this.subscribed) {
            var idx = this.request.types.indexOf(TenderType.PinHigh);
            if (idx != -1) {
                this.request.types.splice(idx, 1);
            }            
            var idx2 = this.request.types.indexOf(TenderType.PinLow);
            if (idx2 != -1) {
                this.request.types.splice(idx2, 1);
            }
        }

        this.paramsBlock.start();
        this.resultsBlock.start();

        var requestToStore = new TenderSearchStorage(this.request);
        requestToStore.publishedDate = this.publishedDate.exportDatesForStorage();
        requestToStore.registerInterestDeadline = this.registerInterestDate.exportDatesForStorage();
        requestToStore.submitDocumentsDeadline = this.submitDocumentsDeadline.exportDatesForStorage();
        requestToStore.contractStartDate = this.contractStartDate.exportDatesForStorage();
        requestToStore.contractEndDate = this.contractEndDate.exportDatesForStorage();
        requestToStore.expiredDate = this.expiredDate.exportDatesForStorage();

        localStorage.setItem(this.requestStorageKey, JSON.stringify(requestToStore));

        var publishedDateRange = this.publishedDate.convertDatesToDto();
        var registerInterestDateRange = this.registerInterestDate.convertDatesToDto();
        var submittedDateRange = this.submitDocumentsDeadline.convertDatesToDto();
        var expiredDateRange = this.expiredDate.convertDatesToDto();
        var contractStartDateRange = this.contractStartDate.convertDatesToDto();
        var contractEndDateRange = this.contractEndDate.convertDatesToDto();

        this.request.publishedDateFrom = publishedDateRange.startDate;
        this.request.publishedDateTo = publishedDateRange.endDate;

        this.request.registerInterestDeadlineFrom = registerInterestDateRange.startDate;
        this.request.registerInterestDeadlineTo = registerInterestDateRange.endDate;

        this.request.contractStartDateFrom = contractStartDateRange.startDate;
        this.request.contractStartDateTo = contractStartDateRange.endDate;

        this.request.contractEndDateFrom = contractEndDateRange.startDate;
        this.request.contractEndDateTo = contractEndDateRange.endDate;

        this.request.submitDocumentsDeadlineFrom = submittedDateRange.startDate;
        this.request.submitDocumentsDeadlineTo = submittedDateRange.endDate;

        this.request.expiredDateFrom = expiredDateRange.startDate;
        this.request.expiredDateTo = expiredDateRange.endDate;

        this.request.supplierId = this.supplierId;

        this.isExpanded = false;
        this.showSaveSearch = true;
        this.hasSearched = true;
        this.updateSearchFitClass();

        return super.search();
    }

    searchCallback(results) {
        super.searchCallback(results);

        this.paramsBlock.stop();

        if (this.resultsView) {
            this.resultsView.onSearchComplete();
        }

        this.resultsBlock.stop();
    }

    resetParameters() {
        this.request.reset();
        this.publishedDate.reset();
        this.registerInterestDate.reset();
        this.submitDocumentsDeadline.reset();
        this.contractStartDate.reset();
        this.contractEndDate.reset();
        this.expiredDate.reset();

        this.setSelectedTypes();

        this.removeAllLocations();
        this.removeAllCategories();

        this.results = null;
        this.showSaveSearch = false;
        this.hasSearched = false;
        this.updateSearchFitClass();
    }

    clearCategories(event) {
        event.stopPropagation();
        this.request.categoryIds = [];
        this.removeAllCategories();
    }

    clearLocations(event) {
        event.stopPropagation();
        this.request.locationIds = [];
        this.removeAllLocations();
    }

    removeAllCategories() {
        this.categories.forEach((c: SelectableCategory) => {
            c.isSelected = false;

            c.children.forEach((child: SelectableCategory) => {
                child.isSelected = false;
            });
        });
        this.categoryFilter = '';
        this.filterCategories();
    }

    removeAllLocations() {
        this.locationsByRegion.regions.forEach((r: SelectableRegion) => {
            r.isSelected = false;
            this.changeRegionSelected(r, this.request.locationIds);
        });

        this.locationsByRegion.locationsWithoutRegion.forEach((l: SelectableLocation) => {
            l.isSelected = false;
            this.changeLocationSelected(l);
        });
    }

    showSearchInfoDialog() {
        $('#search-info-modal').modal();
    }

    showSaveFreeDialog() {
        this.paramsBlock.stop();
        this.resultsBlock.stop();
        $('#search-save-free-modal').modal();
    }

    closeSaveFreeDialog() {
        $('#search-save-free-modal').modal('hide');
        $('body').removeClass('modal-open');
        $('.modal-backdrop').remove();
    }

    showDownloadFreeDialog() {
        this.paidFeatureUpsellService.openDefault();
    }

    removeSavedSearch(id: number) {
        this.accountService.deleteCustomerSavedSearch(id)
            .subscribe(() => {
                //this.ngOnInit();
                this.getSavedSearches();
            });
    }

    includeKeyword(keyword: string) {
        if (keyword.length > 0) {
            keyword.split(/\s*,\s*/).forEach(function (myString) {
                this.request.keywords.push(myString);
            }, this);            
            this.keyword = '';
        }
    }

    excludeKeyword(keyword: string) {
        if (keyword.length > 0) {
            keyword.split(/\s*,\s*/).forEach(function (myString) {
                this.request.excludeKeywords.push(myString);
            }, this);
            this.keyword = '';
        }        
    }

    removeIncludeKeyword(keyword: string) {
        const index = this.request.keywords.indexOf(keyword);
        if (index > -1) {
            this.request.keywords.splice(index, 1);
        }
    }

    removeExcludeKeyword(keyword: string) {
        const index = this.request.excludeKeywords.indexOf(keyword);
        if (index > -1) {
            this.request.excludeKeywords.splice(index, 1);
        }
    }

    async filterCategories() {
        if (this.categoryFilter && this.categoryFilter.length >= 3) {
            var filter = this.categoryFilter.toLowerCase();
            this.categories.forEach(async (category: SelectableCategory) => {
                await this.checkIfCategoryOrChildrenMatch(category, filter);
            });
        } else {
            this.categories.forEach((category: SelectableCategory) => { this.showCategoryAndChildren(category) });
        }
    }

    async checkIfCategoryOrChildrenMatch(category: SelectableCategory, filter: string, parent?: SelectableCategory) {
        category.show = category.name.toLowerCase().indexOf(filter) > -1 ||
            (category.keywords && category.keywords.indexOf(filter) > -1);

        if (parent && category.show) {
            parent.show = true;
            parent.isExpanded = true;
        }

        category.children.forEach(async (child: SelectableCategory) => {
            await this.checkIfCategoryOrChildrenMatch(child, filter, category);
        });
    }

    async showCategoryAndChildren(category: SelectableCategory) {
        category.show = true;

        category.children.forEach((child: SelectableCategory) => { this.showCategoryAndChildren(child) });
    }

    public downloadCsv(): void {        

        if (!this.csvDialogHidden &&
            ((this.results.totalRecords > 200 && this.request.status == NoticeStatus.Live)
                || (this.results.totalRecords > 300 && this.request.status == NoticeStatus.Expired))
        ) {
            $('#search-csv-modal').modal();
        }
        else {
            this.continueDownload();
        }
    }

    public continueDownload(): void {
        $('#search-csv-modal').modal('hide');
        this.clicked = true;
        this.tenderService.downloadCsv(this.request);
        this.clicked = false;
    }

    public hideDialog(id: number) {

        var dto = new AddCustomerHiddenDialogDto();
        dto.cMSDialogId = id;

        this.accountService.addCustomerHiddenDialog(dto).subscribe(() => {
            this.csvDialogHidden = true;
            $('#search-csv-modal').modal('hide');
        }, () => {
        });
    }
}

/** Error when invalid control is dirty, touched, or submitted. */
class InvalidOnlyErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
        return (control && control.invalid);
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/views/tenders/search.component.ts